<template>
	<div>
		<v-main>
			<v-container fluid class="dashboard px-4">
				<PromoSlides />

				<PeriodBar v-model="filters" :platforms="knownPlatforms" :partial="!!summary?.period?.partial" :loading="loading" class="mb-3" @refresh="load" @report="openReport" />

				<v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

				<template v-if="summary">
					<KpiTiles :kpis="summary.kpis" :previous="summary.previousKpis" :deltas="summary.deltas" :series="summary.series" :ads="summary.ads" class="mb-1" />

					<ForecastCard v-if="forecast && isCurrentMonth" :forecast="forecast" class="mb-2" />

					<InsightsToday class="mb-2" />

					<v-row dense>
						<v-col cols="12" lg="8">
							<SalesTrend :series="summary.series" :platforms="summary.platforms" :compare="summary.period.compare" :forecast="isCurrentMonth && forecast ? forecast.days : []" />
						</v-col>
						<v-col cols="12" lg="4">
							<ChannelMix :platforms="summary.platforms" />
						</v-col>
					</v-row>

					<v-row dense>
						<v-col cols="12">
							<TopTables :products="summary.products" :brands="summary.brands" :categories="summary.categories" />
						</v-col>
					</v-row>

					<v-row dense>
						<v-col cols="12">
							<DetailsPanel :status="summary.status" :payments="summary.payments" :countries="summary.countries" />
						</v-col>
					</v-row>
				</template>
				<v-skeleton-loader v-else-if="loading" type="card-heading, image, table" />
			</v-container>
		</v-main>
	</div>
</template>

<script>
import { mapActions } from 'vuex';
import PeriodBar from '@/components/dashboard/PeriodBar.vue';
import PromoSlides from '@/components/dashboard/PromoSlides.vue';
import KpiTiles from '@/components/dashboard/KpiTiles.vue';
import InsightsToday from '@/components/insights/InsightsToday.vue';
import { filtersFromQuery, filtersToParams } from '@/components/dashboard/filters';

const SalesTrend = () => import('@/components/dashboard/SalesTrend.vue');
const ForecastCard = () => import('@/components/dashboard/ForecastCard.vue');
const ChannelMix = () => import('@/components/dashboard/ChannelMix.vue');
const TopTables = () => import('@/components/dashboard/TopTables.vue');
const DetailsPanel = () => import('@/components/dashboard/DetailsPanel.vue');

export default {
	name: 'Home',
	components: { PromoSlides, PeriodBar, KpiTiles, InsightsToday, ForecastCard, SalesTrend, ChannelMix, TopTables, DetailsPanel },
	data() {
		return {
			filters: filtersFromQuery(this.$route.query),
			summary: null,
			forecast: null,
			loading: false,
			error: '',
			knownPlatforms: [],
		};
	},
	computed: {
		// la previsione ha senso solo sul mese in corso
		isCurrentMonth() {
			return this.filters.period === 'month' && !!this.summary?.period?.partial;
		},
	},
	watch: {
		filters(value, old) {
			const params = filtersToParams(value);
			if (JSON.stringify(params) === JSON.stringify(filtersToParams(old))) return;
			this.$router.replace({ query: params }).catch(() => {});
			this.load();
		},
	},
	methods: {
		...mapActions({
			fetchSummary: 'dashboard/asyncFetchSummary',
			fetchForecast: 'dashboard/asyncFetchForecast',
		}),
		async loadForecast() {
			this.forecast = null;
			if (this.filters.period !== 'month') return;
			try {
				this.forecast = await this.fetchForecast(this.filters.platform ? { platform: this.filters.platform } : {});
			} catch (e) {
				// la Home funziona anche senza previsione
				console.log(e);
			}
		},
		async load() {
			if (this.filters.period === 'custom' && (!this.filters.from || !this.filters.to)) return;
			this.loading = true;
			this.error = '';
			this.loadForecast();
			try {
				this.summary = await this.fetchSummary(filtersToParams(this.filters));
				// il filtro canale deve continuare a mostrare tutti i canali anche quando e' attivo
				const names = this.summary.platforms.map(p => p.name);
				this.knownPlatforms = [...new Set([...this.knownPlatforms, ...names])].sort();
			} catch (e) {
				this.error = 'Impossibile caricare la dashboard. Riprova tra qualche istante.';
				console.log(e);
			} finally {
				this.loading = false;
			}
		},
		openReport() {
			const route = this.$router.resolve({ name: 'report', query: filtersToParams(this.filters) });
			window.open(route.href, '_blank');
		},
	},
	created() {
		this.load();
	},
};
</script>

<style scoped>
.dashboard {
	max-width: 1600px;
}
</style>
